import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { AdminGate } from "@/components/AdminGate";
import { getSeoDashboard } from "@/lib/seo-tracking.functions";

export const Route = createFileRoute("/admin/seo")({
  head: () => ({
    meta: [
      { title: "SEO Keyword Tracking & Weekly Report | AutoSpecData" },
      {
        name: "description",
        content:
          "Internal dashboard tracking maintenance-schedule keywords and the make/model pages that need optimization.",
      },
      {
        property: "og:title",
        content: "SEO Keyword Tracking & Weekly Report | AutoSpecData",
      },
      {
        property: "og:description",
        content:
          "Internal dashboard tracking maintenance-schedule keywords and the make/model pages that need optimization.",
      },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  ssr: false,
  component: SeoPage,
});

const fmt = (n: number | null | undefined) =>
  n == null ? "—" : n.toLocaleString();

const priorityClass = (p: string) =>
  p === "high"
    ? "bg-destructive/15 text-destructive"
    : p === "medium"
      ? "bg-primary/15 text-primary"
      : "bg-secondary text-muted-foreground";

function SeoPage() {
  return (
    <AdminGate>
      <SeoDashboardLoader />
    </AdminGate>
  );
}

function SeoDashboardLoader() {
  const fetchDashboard = useServerFn(getSeoDashboard);
  const { data, isPending, error } = useQuery({
    queryKey: ["admin", "seo-dashboard"],
    queryFn: () => fetchDashboard(),
  });
  if (isPending) return <p className="p-8 text-sm text-muted-foreground">Loading…</p>;
  if (error || !data)
    return <p className="p-8 text-sm text-destructive">Could not load the dashboard.</p>;
  return <SeoDashboard keywords={data.keywords} reports={data.reports} />;
}

function SeoDashboard({ keywords, reports }: { keywords: any[]; reports: any[] }) {
  const latest = reports[0] as any | undefined;
  const opportunities = (latest?.page_opportunities ?? []) as any[];

  return (
    <div className="mx-auto max-w-6xl px-4 sm:px-6 py-8">
      <nav className="text-sm text-muted-foreground">
        <Link to="/" className="hover:text-foreground">
          Home
        </Link>
        <span className="mx-2">/</span>
        <span className="text-foreground">SEO tracking</span>
      </nav>

      <h1 className="mt-4 text-3xl font-bold text-foreground">
        SEO Keyword Tracking &amp; Weekly Report
      </h1>
      <p className="mt-3 max-w-3xl text-muted-foreground">
        Tracks the maintenance keyword cluster with Semrush and scores every
        make/model maintenance page so you know which ones to improve next. The
        report refreshes automatically every Monday.
      </p>

      {latest ? (
        <div className="mt-6 rounded-lg border border-border bg-card p-4">
          <p className="text-sm text-muted-foreground">
            Latest report · {latest.period_start} → {latest.period_end}
          </p>
          <p className="mt-1 font-medium text-foreground">{latest.headline}</p>
          {latest.notes && (
            <p className="mt-2 text-sm text-muted-foreground">{latest.notes}</p>
          )}
        </div>
      ) : (
        <div className="mt-6 rounded-lg border border-border bg-card p-4 text-sm text-muted-foreground">
          No report has run yet. The first one lands on the next weekly run.
        </div>
      )}

      <h2 className="mt-10 text-xl font-semibold text-foreground">
        Tracked keywords
      </h2>
      <div className="mt-3 overflow-x-auto rounded-lg border border-border">
        <table className="w-full text-sm">
          <thead className="bg-secondary/50 text-left text-muted-foreground">
            <tr>
              <th className="px-3 py-2 font-medium">Keyword</th>
              <th className="px-3 py-2 font-medium">Cluster</th>
              <th className="px-3 py-2 font-medium">Volume/mo</th>
              <th className="px-3 py-2 font-medium">Difficulty</th>
              <th className="px-3 py-2 font-medium">CPC</th>
              <th className="px-3 py-2 font-medium">Our position</th>
              <th className="px-3 py-2 font-medium">Target page</th>
            </tr>
          </thead>
          <tbody>
            {keywords.map((k: any) => {
              const last = k.history[0];
              const prev = k.history[1];
              const delta =
                last?.volume != null && prev?.volume != null
                  ? last.volume - prev.volume
                  : null;
              return (
                <tr key={k.id} className="border-t border-border">
                  <td className="px-3 py-2 font-medium text-foreground">
                    {k.keyword}
                  </td>
                  <td className="px-3 py-2 text-muted-foreground">{k.cluster}</td>
                  <td className="px-3 py-2">
                    {fmt(last?.volume)}
                    {delta != null && delta !== 0 && (
                      <span
                        className={
                          delta > 0 ? "ml-2 text-primary" : "ml-2 text-destructive"
                        }
                      >
                        {delta > 0 ? "+" : ""}
                        {fmt(delta)}
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-2">{last?.difficulty ?? "—"}</td>
                  <td className="px-3 py-2">
                    {last?.cpc != null ? `$${last.cpc.toFixed(2)}` : "—"}
                  </td>
                  <td className="px-3 py-2">
                    {last?.our_position ?? "Not ranking"}
                  </td>
                  <td className="px-3 py-2 text-muted-foreground">
                    {k.target_path ?? "—"}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <h2 className="mt-10 text-xl font-semibold text-foreground">
        Pages that need optimization
      </h2>
      {opportunities.length === 0 ? (
        <p className="mt-3 text-sm text-muted-foreground">
          Nothing flagged yet — this fills in after the first weekly run.
        </p>
      ) : (
        <ul className="mt-3 space-y-3">
          {opportunities.map((o) => (
            <li
              key={o.path}
              className="rounded-lg border border-border bg-card p-4"
            >
              <div className="flex flex-wrap items-center gap-3">
                <span
                  className={`rounded px-2 py-0.5 text-xs font-medium uppercase ${priorityClass(o.priority)}`}
                >
                  {o.priority}
                </span>
                <a
                  href={o.path}
                  className="font-medium text-foreground hover:underline"
                >
                  {o.make} {o.model} maintenance schedule
                </a>
                <span className="text-sm text-muted-foreground">
                  {o.years} · {o.records} records · score {o.score}
                </span>
              </div>
              <ul className="mt-2 list-disc pl-5 text-sm text-muted-foreground">
                {o.reasons.map((r: string) => (
                  <li key={r}>{r}</li>
                ))}
              </ul>
            </li>
          ))}
        </ul>
      )}

      {reports.length > 1 && (
        <>
          <h2 className="mt-10 text-xl font-semibold text-foreground">
            Previous reports
          </h2>
          <ul className="mt-3 space-y-2 text-sm">
            {reports.slice(1).map((r: any) => (
              <li key={r.id} className="text-muted-foreground">
                <span className="text-foreground">{r.period_end}</span> —{" "}
                {r.headline}
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  );
}
